import { useResetOnClose } from "@/lib/useResetOnClose";
import { BranchTag } from "@/components/BranchTag";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, Banknote, ListChecks , Printer } from "lucide-react";
import { api } from "@/lib/api";
import { openPdf } from "@/lib/print";
import { ProjectFilter, useProjectFilter } from "@/components/procurement/ProjectFilter";
import { usePermissions } from "@/lib/permissions";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import BankAccountSelect from "@/components/BankAccountSelect";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { getErrorMessage } from "@/lib/errors";

import { fmtMoney, fmtDate, partyLabel } from "@/lib/format";
import { CompanyField } from "@/components/CompanyField";
const EMPTY_ALLOCATION = { billType: "purchase_bill_id", bill_id: "", amount: "" };

export default function VendorPayments() {
  const pf = useProjectFilter();
  const { can } = usePermissions();
  const canCreate = can("PAYMENTS", "create");
  const canPost = can("PAYMENTS", "pay");                 // releasing money is its own action (Pay), apart from posting a bill
  const { companies } = useCompany() || {};
  const [payments, setPayments] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [purchaseBills, setPurchaseBills] = useState([]);
  const [serviceBills, setServiceBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [payRes, vendorRes, pbRes, sbRes] = await Promise.all([
        api.get("/procurement/vendor-payments"), api.get("/procurement/vendors"),
        api.get("/procurement/purchase-bills"), api.get("/procurement/service-bills"),
      ]);
      setPayments(payRes.data); setVendors(vendorRes.data); setPurchaseBills(pbRes.data); setServiceBills(sbRes.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const vendorName = (id) => vendors.find((v) => v.id === id)?.name || "—";

  const post = async (payment) => {
    setBusyId(payment.id);
    try {
      await api.post(`/procurement/vendor-payments/${payment.id}/post`);
      toast.success("Payment posted to GL & allocated to bills");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="vendor-payments-page">
      <PageHeader title="Vendor Payments" description="Pay a vendor and allocate the payment against one or more outstanding bills." action={<div className="flex flex-wrap items-center justify-end gap-2"><ProjectFilter filter={pf} />{canCreate && <CreatePaymentDialog companies={companies} vendors={vendors} purchaseBills={purchaseBills} serviceBills={serviceBills} payments={payments} onCreated={load} />}</div>} />
      <div className="rounded-xl border border-border bg-card">
        {!loading && payments.length === 0 ? (
          <EmptyState title="No vendor payments yet" testId="vendor-payments-empty-state" />
        ) : (
          <Table>
            <TableHeader><TableRow><TableHead>Number</TableHead><TableHead>Vendor</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {pf.apply(payments, (r) => r.project_ids).map((p) => (
                <TableRow key={p.id} data-testid={`payment-row-${p.number}`}>
                  <TableCell className="font-mono text-xs text-foreground">{p.number}<BranchTag row={p} /></TableCell>
                  <TableCell className="text-foreground">{vendorName(p.vendor_id)}</TableCell>
                  <TableCell className="text-muted-foreground">{fmtDate(p.payment_date)}</TableCell>
                  <TableCell className="text-right font-mono text-xs font-semibold text-foreground">{fmtMoney(p.amount)}</TableCell>
                  <TableCell><StatusPill value={p.status} /></TableCell>
                  <TableCell className="text-right">
                    {canPost && p.status === "DRAFT" && (
                      <Button size="sm" variant="outline" disabled={busyId === p.id} onClick={() => post(p)} data-testid={`post-payment-button-${p.number}`}>
                        <Banknote className="h-3.5 w-3.5" /> Post
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" title="Print payment voucher" onClick={() => openPdf(`/print/vendor-payments/${p.id}`)} data-testid={`print-payment-${p.number}`}><Printer className="h-3.5 w-3.5" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function CreatePaymentDialog({ companies, vendors, purchaseBills, serviceBills, payments, onCreated }) {
  const EMPTY_FORM = { company_id: "", vendor_id: "", payment_date: "", amount: "", remarks: "", bank_account_id: "" };
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [allocations, setAllocations] = useState([{ ...EMPTY_ALLOCATION }]);
  const [amountTouched, setAmountTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  useResetOnClose(Boolean(open), () => { setForm(EMPTY_FORM); });      // closed without saving: opens fresh next time
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });
  const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  // Amount already sitting in other DRAFT (not yet posted) payments, per bill
  const inDraftPayments = (billId) =>
    (payments || [])
      .filter((p) => p.status === "DRAFT")
      .flatMap((p) => p.allocations || [])
      .filter((a) => a.purchase_bill_id === billId || a.service_bill_id === billId)
      .reduce((sum, a) => sum + Number(a.amount), 0);

  // Only POSTED bills of this vendor that still have something left to pay
  const vendorBills = [
    ...purchaseBills.filter((b) => b.vendor_id === form.vendor_id && b.status === "POSTED")
      .map((b) => ({ bill: b, key: "purchase_bill_id", kind: "Purchase bill", due: Number(b.total_amount) })),
    ...serviceBills.filter((b) => b.vendor_id === form.vendor_id && b.status === "POSTED")
      .map((b) => ({ bill: b, key: "service_bill_id", kind: "Service bill", due: Number(b.net_payable) })),
  ]
    .map((x) => ({
      id: x.bill.id, key: x.key, number: x.bill.number, kind: x.kind, date: x.bill.bill_date, company_id: x.bill.company_id,
      pending: Math.round((x.due - Number(x.bill.paid_amount || 0) - Number(x.bill.adjusted_amount || 0) - inDraftPayments(x.bill.id)) * 100) / 100,
    }))
    .filter((b) => b.pending > 0)
    .sort((a, b) => String(a.date).localeCompare(String(b.date)));

  const billById = (id) => vendorBills.find((b) => b.id === id);
  const allocatedTotal = allocations.reduce((sum, a) => sum + Number(a.amount || 0), 0);

  const applyAllocations = (next) => {
    setAllocations(next);
    if (!amountTouched) {
      const total = next.reduce((sum, a) => sum + Number(a.amount || 0), 0);
      setForm((f) => ({ ...f, amount: total ? total.toFixed(2) : "" }));
    }
  };

  const chooseVendor = (vendorId) => {
    setForm({ ...form, vendor_id: vendorId, amount: amountTouched ? form.amount : "" });
    setAllocations([{ ...EMPTY_ALLOCATION }]);
  };

  const chooseBill = (idx, billId) => {
    const bill = billById(billId);
    if (bill && !form.company_id) setForm((f) => ({ ...f, company_id: bill.company_id }));
    applyAllocations(allocations.map((a, i) => (i === idx
      ? { ...a, bill_id: billId, billType: bill?.key || "purchase_bill_id", amount: bill ? bill.pending.toFixed(2) : "" }
      : a)));
  };

  const setAllocAmount = (idx, value) => applyAllocations(allocations.map((a, i) => (i === idx ? { ...a, amount: value } : a)));

  // Typing the payment Amount spreads it over the selected bills, oldest first (partial payment).
  const changePaymentAmount = (value) => {
    setAmountTouched(true);
    setForm((f) => ({ ...f, amount: value }));
    const chosen = allocations.filter((a) => a.bill_id);
    if (!chosen.length || value === "") return;
    let left = Math.max(Number(value) || 0, 0);
    const byDate = [...chosen].sort((a, b) => String(billById(a.bill_id)?.date).localeCompare(String(billById(b.bill_id)?.date)));
    const share = {};
    for (const a of byDate) {
      const pending = billById(a.bill_id)?.pending || 0;
      const take = Math.min(pending, left);
      share[a.bill_id] = take;
      left = Math.round((left - take) * 100) / 100;
    }
    setAllocations(allocations.map((a) => (a.bill_id ? { ...a, amount: share[a.bill_id] ? share[a.bill_id].toFixed(2) : "0" } : a)));
  };

  const payAllPending = () => {
    if (!vendorBills.length) return;
    if (!form.company_id) setForm((f) => ({ ...f, company_id: vendorBills[0].company_id }));
    applyAllocations(vendorBills.map((b) => ({ billType: b.key, bill_id: b.id, amount: b.pending.toFixed(2) })));
  };

  const problems = [];
  allocations.forEach((a) => {
    const bill = billById(a.bill_id);
    if (bill && Number(a.amount) > bill.pending + 0.005) problems.push(`${bill.number}: ${inr(a.amount)} is more than the pending ${inr(bill.pending)}`);
  });
  if (form.amount && allocatedTotal > Number(form.amount) + 0.005) problems.push(`Allocated ${inr(allocatedTotal)} is more than the payment amount ${inr(form.amount)}`);

  const reset = () => { setForm(EMPTY_FORM); setAllocations([{ ...EMPTY_ALLOCATION }]); setAmountTouched(false); };

  const submit = async (e) => {
    e.preventDefault();
    if (problems.length) { toast.error(problems[0]); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        bank_account_id: form.bank_account_id || null,
        allocations: allocations.filter((a) => a.bill_id && Number(a.amount) > 0).map((a) => ({ [a.billType]: a.bill_id, amount: a.amount })),
      };
      await api.post("/procurement/vendor-payments", payload);
      toast.success("Vendor payment created");
      reset();
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild><Button data-testid="create-payment-button"><Plus className="h-4 w-4" /> New Payment</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto overflow-x-hidden sm:max-w-2xl">
        <DialogHeader><DialogTitle>New Vendor Payment</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3.5" data-testid="create-payment-form">
          <div className="grid grid-cols-2 gap-3">
            <CompanyField label="Company" value={form.company_id} onChange={(v) => setForm({ ...form, company_id: v })} testId="payment-company-select" />
            <div>
              <Label>Vendor</Label>
              <Select required value={form.vendor_id} onValueChange={chooseVendor}>
                <SelectTrigger data-testid="payment-vendor-select" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{(vendors || []).map((v) => <SelectItem key={v.id} value={v.id}>{partyLabel(v)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label>Bills to pay</Label>
              {form.vendor_id && vendorBills.length > 0 && (
                <Button type="button" size="sm" variant="ghost" onClick={payAllPending} data-testid="pay-all-pending-button">
                  <ListChecks className="h-3.5 w-3.5" /> Pay all pending ({inr(vendorBills.reduce((s, b) => s + b.pending, 0))})
                </Button>
              )}
            </div>
            {!form.vendor_id ? (
              <p className="mt-1.5 text-xs text-muted-foreground">Choose a vendor to see their unpaid bills.</p>
            ) : vendorBills.length === 0 ? (
              <p className="mt-1.5 text-xs text-muted-foreground">This vendor has no unpaid posted bills. You can still record an on-account payment.</p>
            ) : (
              <div className="mt-1.5 space-y-2">
                {allocations.map((a, idx) => {
                  const chosenElsewhere = new Set(allocations.filter((_, i) => i !== idx).map((x) => x.bill_id).filter(Boolean));
                  const options = vendorBills.filter((b) => !chosenElsewhere.has(b.id));
                  const bill = billById(a.bill_id);
                  return (
                    <div key={idx} className="space-y-1" data-testid={`payment-allocation-row-${idx}`}>
                      <div className="flex min-w-0 items-center gap-2">
                        <Select value={a.bill_id} onValueChange={(v) => chooseBill(idx, v)}>
                          <SelectTrigger className="min-w-0 flex-1 [&>span]:truncate" data-testid={`payment-allocation-bill-select-${idx}`}><SelectValue placeholder="Select a bill" /></SelectTrigger>
                          <SelectContent>
                            {options.map((b) => (
                              <SelectItem key={b.id} value={b.id}>{b.number} · {b.kind === "Service bill" ? "Service" : "Purchase"} · {inr(b.pending)}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input type="number" step="0.01" min="0" placeholder="Amount" className="w-32 shrink-0" value={a.amount}
                          onChange={(e) => setAllocAmount(idx, e.target.value)} data-testid={`payment-allocation-amount-input-${idx}`} />
                        {allocations.length > 1 && (
                          <Button type="button" size="sm" variant="ghost" onClick={() => applyAllocations(allocations.filter((_, i) => i !== idx))}>
                            <Trash2 className="h-3.5 w-3.5 text-rose-400" />
                          </Button>
                        )}
                      </div>
                      {bill && (
                        <p className="pl-1 text-[11px] text-muted-foreground">
                          {bill.kind} dated {bill.date} · pending {inr(bill.pending)}{Number(a.amount) > 0 && Number(a.amount) < bill.pending ? ` · ${inr(bill.pending - Number(a.amount))} will remain` : ""}
                        </p>
                      )}
                    </div>
                  );
                })}
                {allocations.length < vendorBills.length && (
                  <Button type="button" size="sm" variant="outline" onClick={() => setAllocations([...allocations, { ...EMPTY_ALLOCATION }])} data-testid="add-payment-allocation-button">
                    <Plus className="h-3.5 w-3.5" /> Add another bill
                  </Button>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><Label>Date</Label><Input required type="date" data-testid="payment-date-input" value={form.payment_date} onChange={set("payment_date")} className="mt-1.5" /></div>
            <div>
              <Label>Amount <span className="font-normal text-muted-foreground">{amountTouched ? "(entered)" : "(total of bills)"}</span></Label>
              <Input required type="number" step="0.01" min="0.01" data-testid="payment-amount-input" value={form.amount}
                onChange={(e) => changePaymentAmount(e.target.value)} className="mt-1.5" />
              {form.amount && allocations.some((a) => a.bill_id) && (
                <p className="mt-1 text-[11px] text-muted-foreground">Spread over the selected bills, oldest first. You can also change each bill's amount above.</p>
              )}
            </div>
          </div>
          <BankAccountSelect companyId={form.company_id} value={form.bank_account_id} onChange={(v) => setForm({ ...form, bank_account_id: v })} label="Paid from" testId="payment-bank-select" />
          <div><Label>Remarks</Label><Input value={form.remarks} onChange={set("remarks")} placeholder="Cheque / UTR number, bank…" className="mt-1.5" /></div>

          {problems.length > 0 && (
            <ul className="space-y-1 rounded-lg bg-rose-500/10 p-2.5 text-xs text-rose-700 dark:text-rose-400">{problems.map((p, i) => <li key={i}>{p}</li>)}</ul>
          )}
          <DialogFooter>
            <Button type="submit" disabled={saving || problems.length > 0} data-testid="create-payment-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
